import { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import useAuth from '@/contexts/useAuth';
import { dynamoDBService, FeedbackWeeklyStats } from '@/utils/dynamodb';

// Centralised font styles
// Tick labels slightly smaller; axis titles (label) larger for readability
const tickFont = {
  fontFamily: 'var(--app-font, var(--font-sans, system-ui, sans-serif))',
  fontSize: 13,
  fontWeight: 500,
  fill: '#1f2937',
};

const axisLabelFont = {
  ...tickFont,
  fontSize: 15,
  fontWeight: 600,
};

const axisLineStyle = { stroke: '#1f2937', strokeWidth: 2 };
const tickLineStyle = { stroke: '#1f2937', strokeWidth: 2 };

// Series colours (reuse for axis label colouring)
const POSITIVE_SERIES_COLOR = 'rgba(23, 62, 119, 1)';
const TOTAL_SERIES_COLOR = 'rgba(121, 195, 190, 1)';

export default function FeedbackChart() {
  const { getCredentials } = useAuth();
  const [feedbackData, setFeedbackData] = useState<FeedbackWeeklyStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFeedbackStats = useCallback(
    async (refresh: boolean = false) => {
      try {
        setIsLoading(true);
        setError(null);
        const credentials = await getCredentials();
        await dynamoDBService.initializeClient(credentials);
        const data = await dynamoDBService.getFeedbackWeeklyStats(refresh);
        setFeedbackData(data);
      } catch (err: any) {
        console.error('Failed to load feedback stats:', err);
        setError(err.message || 'Failed to load feedback statistics');
        setFeedbackData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [getCredentials],
  );

  useEffect(() => {
    loadFeedbackStats(false);
  }, [loadFeedbackStats]);

  const displayData = feedbackData;
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>User Feedback Weekly Stats</span>
          <button
            onClick={() => loadFeedbackStats(true)}
            disabled={isLoading}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw
              className={`h-3 w-3 mr-1 ${isLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </CardTitle>
        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-96 flex items-center justify-center">
            <div className="flex items-center gap-3 text-gray-600">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span>Loading feedback statistics...</span>
            </div>
          </div>
        ) : error ? (
          <div className="h-96 flex items-center justify-center">
            <div className="text-red-600 text-lg font-semibold">
              Failed to load feedback statistics.
            </div>
          </div>
        ) : (
          /* Wrapper: allow hover (tooltips) but suppress focus & clicks */
          <div
            tabIndex={-1}
            onMouseDown={(e) => {
              // Prevent element from receiving focus on mousedown
              e.preventDefault();
            }}
            onClick={(e) => {
              // Swallow click events so parent layouts don't react
              e.stopPropagation();
            }}
            style={{ userSelect: 'none', outline: 'none' }}
            aria-label="Feedback statistics chart"
          >
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart
                data={displayData}
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              >
                <CartesianGrid stroke="#f0f0f0" />
                <XAxis
                  dataKey="weekStart"
                  tick={tickFont}
                  axisLine={axisLineStyle}
                  tickLine={tickLineStyle}
                  tickFormatter={(date: string) => {
                    const d = new Date(date);
                    return d.toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    });
                  }}
                />
                <YAxis
                  yAxisId="left"
                  tick={tickFont}
                  axisLine={axisLineStyle}
                  tickLine={tickLineStyle}
                  padding={{ bottom: 1 }}
                  domain={[0, 'dataMax']}
                  tickFormatter={(value: number) => Math.ceil(value).toString()}
                  label={{
                    value: 'Total Responses',
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      ...axisLabelFont,
                      fill: TOTAL_SERIES_COLOR,
                      textAnchor: 'middle',
                    },
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={tickFont}
                  axisLine={axisLineStyle}
                  tickLine={tickLineStyle}
                  padding={{ bottom: 1 }}
                  label={{
                    value: '% Positive Responses',
                    angle: 90,
                    position: 'insideRight',
                    style: {
                      ...axisLabelFont,
                      fill: POSITIVE_SERIES_COLOR,
                      textAnchor: 'middle',
                    },
                  }}
                  domain={[0, 100]}
                  tickFormatter={(value: number) => `${value}%`}
                />
                <Tooltip
                  contentStyle={{
                    fontFamily: tickFont.fontFamily,
                    fontSize: tickFont.fontSize,
                    fontWeight: tickFont.fontWeight,
                  }}
                  formatter={(value, name) =>
                    name === 'positivePct' ? `${value}%` : value
                  }
                  labelFormatter={(label) => `Week starting: ${label}`}
                />
                <Bar
                  yAxisId="left"
                  dataKey="totalResponses"
                  fill={TOTAL_SERIES_COLOR}
                  barSize={70}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="positivePct"
                  stroke={POSITIVE_SERIES_COLOR}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
